import { useEffect, useRef } from 'react';
import { Animated, DimensionValue, Easing, StyleProp, ViewStyle } from 'react-native';

import { colors, radii } from '@/constants/theme';

type RadiusToken = keyof typeof radii;

export type SkeletonProps = {
  width?: DimensionValue;
  height?: number;
  /** Theme radius token. Default `'sm'`. Pass `'full'` for circular avatars. */
  radius?: RadiusToken;
  /** Disable the pulse (useful in tests / reduced motion). */
  animated?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function Skeleton({
  width = '100%',
  height = 16,
  radius = 'sm',
  animated = true,
  style,
  testID,
}: SkeletonProps) {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    if (!animated) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.7,
          duration: 500,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 500,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
    };
  }, [animated, opacity]);

  return (
    <Animated.View
      testID={testID}
      style={[
        {
          width,
          height,
          borderRadius: radii[radius],
          backgroundColor: colors.border,
          opacity: animated ? opacity : 0.6,
        },
        style,
      ]}
    />
  );
}
